/**
 * @file Слой 0 - Потоки связей
 * @module membrane/src/layers/Layer0Flow
 * 
 * Альтернатива сфере - поток связей.
 * Поток не имеет жёстких границ.
 * Лучи указывают возможные направления движения.
 * Между лучами появляются слова-связки.
 * Наведение на слово может сопровождаться звуком / нотой.
 * Из потока можно перейти в сферу.
 */

import { motion } from 'framer-motion';
import { BaseLayerProps } from '../types/membrane';
import { useState, useRef, useEffect } from 'react';
import { getLink } from '../services/mirrorApi';

/**
 * Компонент Layer0Flow - потоки связей
 * 
 * Пользователь в потоке:
 * - Поток без жёстких границ
 * - Лучи указывают направления
 * - Слова-связки между лучами (подсвечиваются)
 * - Наведение на слово - звук/нота/перелив звуков
 * - Поворот к сфере - можно кликнуть
 * - Клик на сферу - переход внутрь
 */
interface Layer0FlowProps extends BaseLayerProps {
  linkId?: string;
}

export default function Layer0Flow({ onProceed, onReturn, state: _state, linkId = '1' }: Layer0FlowProps) {
  const [hoveredWord, setHoveredWord] = useState<string | null>(null);
  const [hoveredSphere, setHoveredSphere] = useState<number | null>(null);
  const [rays, setRays] = useState<Array<{ id: number; angle: number; word: string; frequency: number; soundType: 'note' | 'chord' }>>([]);
  const [availableSpheres, setAvailableSpheres] = useState<Array<{ id: number; angle: number; distance: number }>>([]);
  const [loading, setLoading] = useState(true);
  const audioContextRef = useRef<AudioContext | null>(null);

  // Инициализация аудио контекста для звуков
  useEffect(() => {
    if (typeof window !== 'undefined' && 'AudioContext' in window) {
      audioContextRef.current = new AudioContext();
    }
  }, []);

  // Загрузка данных потока через Mirror API
  useEffect(() => {
    const loadLinkData = async () => {
      try {
        setLoading(true);
        const linkData = await getLink(linkId);
        
        // Преобразуем направления в формат rays
        const newRays = linkData.directions.map((dir: any, index: number) => ({
          id: index + 1,
          angle: dir.angle,
          word: dir.word,
          frequency: dir.frequency,
          soundType: dir.soundType,
        }));
        setRays(newRays);
        
        // Преобразуем сферы
        const newSpheres = linkData.spheres.map((sph: any, index: number) => ({
          id: index + 1,
          angle: sph.angle,
          distance: sph.distance,
        }));
        setAvailableSpheres(newSpheres);
      } catch (error) {
        console.error('Error loading link data:', error);
        // Fallback к дефолтным данным
        setRays([
          { id: 1, angle: 0, word: 'Пространство', frequency: 220, soundType: 'note' as const },
          { id: 2, angle: 45, word: 'Связь', frequency: 247, soundType: 'chord' as const },
          { id: 3, angle: 90, word: 'Глубина', frequency: 262, soundType: 'note' as const },
        ]);
        setAvailableSpheres([
          { id: 1, angle: 30, distance: 35 },
          { id: 2, angle: 120, distance: 40 },
        ]);
      } finally {
        setLoading(false);
      }
    };

    loadLinkData();
  }, [linkId]);

  // Воспроизведение звука/ноты при наведении на слово
  const playSound = (frequency: number, type: 'note' | 'chord' = 'note') => {
    if (!audioContextRef.current) return;

    const ctx = audioContextRef.current;
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);

    if (type === 'chord') {
      // Перелив звуков (аккорд)
      const frequencies = [frequency, frequency * 1.25, frequency * 1.5];
      frequencies.forEach((freq, index) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.value = freq;
        osc.type = 'sine';
        gain.gain.setValueAtTime(0.1, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);
        osc.start(ctx.currentTime + index * 0.1);
        osc.stop(ctx.currentTime + 0.5 + index * 0.1);
      });
    } else {
      // Одна нота
      oscillator.frequency.value = frequency;
      oscillator.type = 'sine';
      gainNode.gain.setValueAtTime(0.1, ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);
      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + 0.5);
    }
  };


  // Обработка наведения на слово (звук/нота)
  const handleWordHover = (word: string, frequency: number, soundType: 'note' | 'chord') => {
    setHoveredWord(word);
    playSound(frequency, soundType);
  };

  // Обработка клика по слову
  const handleWordClick = (_rayId: number) => {
    // Можно добавить дополнительное взаимодействие
  };

  // Обработка клика по сфере из потока
  const handleSphereClick = () => {
    if (onProceed) {
      onProceed();
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 1.5, ease: 'easeInOut' }}
      className="absolute inset-0 w-full h-full overflow-hidden"
      style={{
        background: 'radial-gradient(ellipse at center, hsl(220, 50%, 90%) 0%, hsl(240, 45%, 85%) 50%, hsl(260, 40%, 80%) 100%)',
      }}
    >
      {/* Индикатор загрузки */}
      {loading && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="absolute inset-0 flex items-center justify-center"
        >
          <div className="text-white/40 text-sm font-light">Загрузка потока...</div>
        </motion.div>
      )}

      {/* Лучи-направления (без жёстких границ) */}
      <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ zIndex: 1 }}>
        {rays.map((ray) => {
          const angleRad = (ray.angle * Math.PI) / 180;
          const length = 35;
          const x1 = 50;
          const y1 = 50;
          const x2 = 50 + Math.cos(angleRad) * length;
          const y2 = 50 + Math.sin(angleRad) * length;

          return (
            <g key={ray.id}>
              <motion.line
                x1={`${x1}%`}
                y1={`${y1}%`}
                x2={`${x2}%`}
                y2={`${y2}%`}
                stroke={hoveredWord === ray.word ? 'rgba(200, 220, 255, 0.6)' : 'rgba(200, 220, 255, 0.25)'}
                strokeWidth={hoveredWord === ray.word ? '3' : '1.5'}
                strokeDasharray={hoveredWord === ray.word ? '0' : '5,5'}
                initial={{ pathLength: 0, opacity: 0 }}
                animate={{
                  pathLength: 1,
                  opacity: hoveredWord === ray.word ? 0.8 : 0.4,
                }}
                transition={{ duration: 1, delay: ray.id * 0.1 }}
              />
              {/* Слово-связка между лучами (лучисто направленное) */}
              <motion.text
                x={`${x2 + Math.cos(angleRad) * 3}%`}
                y={`${y2 + Math.sin(angleRad) * 3}%`}
                fill={hoveredWord === ray.word ? 'rgba(200, 220, 255, 1)' : 'rgba(200, 220, 255, 0.5)'}
                fontSize="16"
                fontFamily="sans-serif"
                fontWeight="300"
                className="pointer-events-auto cursor-pointer"
                onMouseEnter={() => handleWordHover(ray.word, ray.frequency, ray.soundType)}
                onMouseLeave={() => setHoveredWord(null)}
                onClick={() => handleWordClick(ray.id)}
                initial={{ opacity: 0 }}
                animate={{ 
                  opacity: hoveredWord === ray.word ? 1 : 0.6,
                  scale: hoveredWord === ray.word ? 1.2 : 1,
                }}
                transition={{ duration: 0.3 }}
                style={{
                  textShadow: hoveredWord === ray.word 
                    ? '0 0 15px rgba(200, 220, 255, 0.8)' 
                    : 'none',
                }}
              >
                {ray.word}
              </motion.text>
            </g>
          );
        })}
      </svg>

      {/* Сферы, доступные из потока (можно повернуться к ним) */}
      {availableSpheres.map((sphere) => {
        const angleRad = (sphere.angle * Math.PI) / 180;
        const xPos = 50 + Math.cos(angleRad) * sphere.distance;
        const yPos = 50 + Math.sin(angleRad) * sphere.distance;

        return (
          <motion.div
            key={sphere.id}
            className="absolute rounded-full cursor-pointer"
            style={{
              left: `${xPos}%`,
              top: `${yPos}%`,
              width: '80px',
              height: '80px',
              transform: 'translate(-50%, -50%)',
              zIndex: 2,
            }}
            initial={{ opacity: 0, scale: 0 }}
            animate={{
              opacity: hoveredSphere === sphere.id ? 0.6 : 0.3,
              scale: hoveredSphere === sphere.id ? 1.2 : 1,
            }}
            transition={{ duration: 0.3 }}
            onMouseEnter={() => setHoveredSphere(sphere.id)}
            onMouseLeave={() => setHoveredSphere(null)}
            onClick={handleSphereClick}
          >
            <div
              className="w-full h-full rounded-full border-2"
              style={{
                borderColor: hoveredSphere === sphere.id
                  ? 'rgba(200, 220, 255, 0.8)'
                  : 'rgba(200, 220, 255, 0.3)',
                background: hoveredSphere === sphere.id
                  ? 'radial-gradient(circle, rgba(200, 220, 255, 0.2) 0%, transparent 70%)'
                  : 'radial-gradient(circle, rgba(200, 220, 255, 0.1) 0%, transparent 70%)',
                boxShadow: hoveredSphere === sphere.id
                  ? '0 0 30px rgba(200, 220, 255, 0.5)'
                  : '0 0 10px rgba(200, 220, 255, 0.2)',
              }}
            />
          </motion.div>
        );
      })}

      {/* Центральная точка потока */}
      <motion.div
        className="absolute rounded-full"
        style={{
          left: '50%',
          top: '50%',
          width: '15px',
          height: '15px',
          transform: 'translate(-50%, -50%)',
          background: 'radial-gradient(circle, rgba(200, 220, 255, 0.9) 0%, transparent 70%)',
          boxShadow: '0 0 25px rgba(200, 220, 255, 0.6)',
          zIndex: 3,
        }}
        animate={{
          scale: [1, 1.3, 1],
          opacity: [0.7, 1, 0.7],
        }}
        transition={{
          duration: 2.5,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />

      {/* Подсказка */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.3 }}
        transition={{ delay: 2 }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-white/30 text-xs font-light text-center"
      >
        Наведите на слово (звук) • Повернитесь к сфере • Клик для перехода • Esc для выхода
      </motion.div>

      {/* Кнопка возврата */}
      {onReturn && (
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.3 }}
          whileHover={{ opacity: 0.5 }}
          onClick={onReturn}
          className="absolute top-8 left-8 text-white/40 hover:text-white/60 transition-opacity z-10"
          aria-label="Вернуться"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 19l-7-7 7-7" />
          </svg>
        </motion.button>
      )}
    </motion.div>
  );
}
